'use client';

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { PayPeriod } from '../page';

interface PayPeriodComparisonProps {
  periods: PayPeriod[];
  currentPeriodId: string;
}

const fmt = (n: number) => `$${n.toLocaleString()}`;
const fmtShort = (n: number) => `$${(n / 1000).toFixed(1)}k`;

function DeltaBadge({ current, previous }: { current: number; previous: number }) {
  if (previous === 0) return null;
  const pct = (((current - previous) / previous) * 100).toFixed(1);
  const isUp = current >= previous;
  const isFlat = current === previous;
  if (isFlat) return <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground"><Minus size={11} />0%</span>;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-600 ${isUp ? 'text-positive' : 'text-danger'}`}>
      {isUp ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
      {isUp ? '+' : ''}{pct}%
    </span>
  );
}

export default function PayPeriodComparison({ periods, currentPeriodId }: PayPeriodComparisonProps) {
  const current = periods.find((p) => p.id === currentPeriodId) ?? periods[0];
  const others = periods.filter((p) => p.id !== currentPeriodId);

  // Chart data — all periods for bar chart
  const chartData = [...periods].reverse().map((p) => ({
    name: p.label.split(',')[0], // short label
    'Gross Pay': p.totalGross,
    'Net Pay': p.totalNet,
    'Deductions': p.totalDeductions,
    'OT Cost': p.overtimeCost,
  }));

  const hoursData = [...periods].reverse().map((p) => ({
    name: p.label.split(',')[0],
    'Regular Hours': p.totalHours - p.overtimeHours,
    'Overtime Hours': p.overtimeHours,
  }));

  const metrics: { label: string; key: keyof PayPeriod; format: (n: number) => string; higherIsBetter?: boolean }[] = [
    { label: 'Total Staff', key: 'totalStaff', format: (n) => String(n), higherIsBetter: true },
    { label: 'Total Hours', key: 'totalHours', format: (n) => n.toLocaleString(), higherIsBetter: true },
    { label: 'Gross Pay', key: 'totalGross', format: fmt, higherIsBetter: true },
    { label: 'Deductions', key: 'totalDeductions', format: fmt, higherIsBetter: false },
    { label: 'Net Pay', key: 'totalNet', format: fmt, higherIsBetter: true },
    { label: 'OT Hours', key: 'overtimeHours', format: (n) => String(n), higherIsBetter: false },
    { label: 'OT Cost', key: 'overtimeCost', format: fmt, higherIsBetter: false },
  ];

  return (
    <div className="space-y-4">
      {/* Comparison table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-700 text-foreground">Period-over-Period Comparison</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Comparing <span className="font-600 text-foreground">{current.label}</span> against previous pay periods</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-5 py-3 text-left text-xs font-600 text-muted-foreground uppercase tracking-wide">Metric</th>
                <th className="px-5 py-3 text-right text-xs font-600 text-primary uppercase tracking-wide whitespace-nowrap">
                  {current.label} <span className="text-[10px] font-500 bg-primary/10 text-primary px-1.5 py-0.5 rounded-md ml-1">Current</span>
                </th>
                {others.map((p) => (
                  <th key={p.id} className="px-5 py-3 text-right text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                    {p.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={m.key} className="border-b border-border row-hover transition-colors">
                  <td className="px-5 py-3 font-500 text-foreground">{m.label}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-700 text-foreground">
                    {m.format(current[m.key] as number)}
                  </td>
                  {others.map((p) => {
                    const cv = current[m.key] as number;
                    const pv = p[m.key] as number;
                    return (
                      <td key={p.id} className="px-5 py-3 text-right tabular-nums text-muted-foreground">
                        <div className="flex flex-col items-end gap-0.5">
                          <span>{m.format(pv)}</span>
                          <DeltaBadge current={cv} previous={pv} />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Pay breakdown chart */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-700 text-foreground mb-1">Pay Breakdown by Period</h3>
          <p className="text-xs text-muted-foreground mb-4">Gross, net, deductions, and overtime cost</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} barSize={16} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(value: number, name: string) => [fmt(value), name]}
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Gross Pay" fill="var(--primary)" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Net Pay" fill="var(--positive)" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Deductions" fill="var(--danger)" radius={[3, 3, 0, 0]} />
              <Bar dataKey="OT Cost" fill="var(--warning)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Hours chart */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-700 text-foreground mb-1">Hours by Period</h3>
          <p className="text-xs text-muted-foreground mb-4">Regular vs overtime hours comparison</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={hoursData} barSize={20} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Regular Hours" fill="var(--primary)" radius={[3, 3, 0, 0]} stackId="a" />
              <Bar dataKey="Overtime Hours" fill="var(--warning)" radius={[3, 3, 0, 0]} stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
